import { useState } from 'react'
import type { FormEvent } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'motion/react'
import { Gift, Loader2 } from 'lucide-react'
import { Link } from 'react-router-dom'
import { supabase } from '@/integrations/supabase/client'
import { useLanguage } from '@/context/LanguageContext'

const t = {
  it: {
    titolo: 'Ci sei quasi',
    sub: 'Completa il tuo profilo per iniziare a creare la tua wishlist.',
    linkPubblico: 'Link pubblico',
    linkSub: "È l'indirizzo web della tua wishlist da condividere con amici e parenti. Non si usa per il login: sceglilo con cura perché si può cambiare una sola volta.",
    tuoLink: 'Il tuo link:',
    titoloWishlist: 'Titolo della wishlist',
    facoltativo: '(facoltativo)',
    titoloPh: 'Es. I regali per il mio compleanno 🎂',
    salvataggio: 'Salvataggio…',
    inizia: 'Inizia',
    usernameInvalid: 'Username non valido: 3-20 caratteri, lettere, numeri e underscore.',
    usernameTaken: 'Questo username è già in uso, provane un altro 💡',
    placeholder: 'mario_rossi',
  },
  en: {
    titolo: 'Almost there',
    sub: 'Complete your profile to start creating your wishlist.',
    linkPubblico: 'Public link',
    linkSub: "This is the web address of your wishlist to share with friends and family. It's not used for login: choose carefully because it can only be changed once.",
    tuoLink: 'Your link:',
    titoloWishlist: 'Wishlist title',
    facoltativo: '(optional)',
    titoloPh: 'e.g. Gifts for my birthday 🎂',
    salvataggio: 'Saving…',
    inizia: 'Get started',
    usernameInvalid: 'Invalid username: 3-20 characters, letters, numbers and underscores.',
    usernameTaken: 'This username is already taken, try another one 💡',
    placeholder: 'mario_rossi',
  },
}

const USERNAME_RE = /^[a-zA-Z0-9_]{3,20}$/

export default function OnboardingPage() {
  const navigate = useNavigate()
  const { language } = useLanguage()
  const tt = t[language]
  const [username, setUsername] = useState('')
  const [title, setTitle] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)

  const usernameValue = username.trim()

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (saving) return
    setError(null)

    if (!USERNAME_RE.test(usernameValue)) {
      setError(tt.usernameInvalid)
      return
    }

    setSaving(true)
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) {
      setSaving(false)
      navigate('/login', { replace: true })
      return
    }

    const { data: clash } = await supabase
      .from('profiles')
      .select('id')
      .ilike('username', usernameValue)
      .neq('id', user.id)
      .maybeSingle()

    if (clash) {
      setSaving(false)
      setError(tt.usernameTaken)
      return
    }

    const fullName =
      (user.user_metadata?.full_name as string | undefined) ||
      (user.user_metadata?.name as string | undefined) ||
      null

    const { error: upError } = await supabase
      .from('profiles')
      .upsert({
        id: user.id,
        username: usernameValue,
        wishlist_title: title.trim() || null,
        display_name: fullName,
      })

    setSaving(false)
    if (upError) {
      setError(upError.message)
      return
    }
    navigate('/dashboard', { replace: true })
  }

  return (
    <div className="min-h-screen relative flex items-center justify-center px-4 py-12">
      <div
        className="absolute inset-0 bg-cover bg-center bg-fixed"
        style={{ backgroundImage: 'url(/images/colorful-bg.webp)' }}
      />

      <motion.div
        className="relative w-full max-w-md glass rounded-lg p-8 sm:p-10 overflow-hidden"
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <Link to="/" className="flex items-center justify-center gap-2 font-display text-lg" onClick={() => window.scrollTo(0, 0)}>
          <span className="w-9 h-9 rounded-lg bg-neutral-900 text-white flex items-center justify-center">
            <Gift className="w-5 h-5" />
          </span>
          Make me a Gift
        </Link>

        <h1 className="mt-6 text-3xl font-extrabold tracking-tight text-center">
          {tt.titolo}
        </h1>
        <p className="mt-2 text-sm text-muted-foreground text-center">
          {tt.sub}
        </p>

        {error && (
          <div className="mt-6 rounded-2xl bg-red-50 border border-red-100 text-red-700 text-sm px-4 py-3">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="mt-6 space-y-4">
          <div>
            <label htmlFor="username" className="block text-sm font-semibold mb-1.5">
              {tt.linkPubblico}
            </label>
            <div className="flex items-center rounded-xl border border-border bg-white focus-within:ring-2 focus-within:ring-primary focus-within:border-primary transition">
              <span className="pl-4 text-muted-foreground/60 font-semibold text-xs sm:text-sm">makemeagift.shop/w/</span>
              <input
                id="username"
                type="text"
                required
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder={tt.placeholder}
                className="w-full rounded-xl px-2 py-3 text-sm outline-none placeholder:text-muted-foreground/60"
              />
            </div>
            <p className="mt-1.5 text-xs text-muted-foreground">
              {tt.linkSub}
            </p>
            {usernameValue && USERNAME_RE.test(usernameValue) && (
              <p className="mt-1 text-xs text-muted-foreground">
                {tt.tuoLink} <span className="font-semibold text-primary">{window.location.origin}/w/{usernameValue}</span>
              </p>
            )}
          </div>

          <div>
            <label htmlFor="wishlist-title" className="block text-sm font-semibold mb-1.5">
              {tt.titoloWishlist} <span className="font-normal text-muted-foreground/60">{tt.facoltativo}</span>
            </label>
            <input
              id="wishlist-title"
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={tt.titoloPh}
              className="w-full rounded-xl border border-border bg-white px-4 py-3 text-sm outline-none transition focus:ring-2 focus:ring-primary focus:border-primary placeholder:text-muted-foreground/60"
            />
          </div>

          <button
            type="submit"
            disabled={saving}
            className="w-full text-sm font-medium text-white bg-neutral-900 rounded-full px-5 py-2.5 hover:bg-neutral-800 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {saving && <Loader2 className="w-5 h-5 animate-spin" />}
            {saving ? tt.salvataggio : tt.inizia}
          </button>
        </form>
      </motion.div>
    </div>
  )
}
